import { IFinalResponse, Utils } from '../Core';
import Machines from '../Flows';
import {
  CountriesTypes,
  GeoLocationTypes,
  HeadlessDeliveryTypes,
  PartnerTokenTypes,
  UploadFileS3Types,
  ZipCodeTypes,
  ListOfValuesTypes,
  CmsContentTypes,
  CmsDeliveryTypes,
  ListPartnerTypeTypes,
  CommonContentTypes,
} from '../Types';

/**
 * @description Busca dados no mapa por geolocalização, endereço, localização predetiva, etc
 *
 * @param params GeoLocationServiceRequestType
 * @returns Promise IFinalResponse
 */
export const getGeoLocation = (
  params: GeoLocationTypes.GeoLocationServiceRequestType,
): Promise<IFinalResponse> =>
  Utils.callServiceMachine(Machines.onGeoLocation.machine(), params);

/**
 * @description Cria busca interativa no Liferay (CMS)
 *
 * @param params HeadlessDeliveryServiceRequestType
 * @returns Promise IFinalResponse
 */
export const findHeadlessDelivery = (
  params: HeadlessDeliveryTypes.HeadlessDeliveryServiceRequestType,
): Promise<IFinalResponse> =>
  Utils.callServiceMachine(Machines.onHeadlessDelivery.machine(), params);

/**
 * @description Este serviço é parecido com o findHeadlessDelivery, porém tenta deixar os parametros de entrada como argumento
 *
 * @param params keys, fields
 * @returns Promise IFinalResponse
 */
export const getHeadlessDelivery = (
  keys: string[],
  fields?: string[],
): Promise<IFinalResponse> => {
  const filter: HeadlessDeliveryTypes.HeadlessDeliverySearchType[] = keys.map(
    (value) => ({
      param: 'friendlyUrlPath',
      conditional: 'eq',
      value,
    }),
  );

  return Utils.callServiceMachine(Machines.onHeadlessDelivery.machine(), {
    flatter: true,
    filter,
    fields,
  });
};

/**
 * @description Upload de arquivos no S3 por diretorio pré-definido
 *
 * @param params UploadFileS3ServiceRequestType
 * @returns Promise IFinalResponse
 */
export const uploadFileS3 = (
  params: UploadFileS3Types.UploadFileS3ServiceRequestType,
): Promise<IFinalResponse> =>
  Utils.callServiceMachine(Machines.onUploadFileS3.machine(), params);

/**
 * @description Busca URL do Parceiro
 *
 * @param params PartnerTokenServiceRequestType
 * @returns Promise IPartnerTokenServiceResponse
 */
export const partnerToken = (
  params: PartnerTokenTypes.PartnerTokenServiceRequestType,
): Promise<PartnerTokenTypes.IPartnerTokenServiceResponse> =>
  Utils.callServiceMachine(Machines.onPartnerToken.machine(), params);

/**
 * @description Lista de países com nome visual, nome para o Siebel, sigla iso2, sigla iso3 e DDI.
 *
 * @param params CountriesServiceRequestType
 * @returns Promise ICountriesServiceResponse
 */
export const getListCountry = (
  params?: CountriesTypes.CountriesServiceRequestType,
): Promise<CountriesTypes.ICountriesServiceResponse> =>
  Utils.callServiceMachine(Machines.onGetListCountry.machine(), params);

/**
 * @description Pega dados de endereço do ZipCode informado (Ex. CEP)
 *
 * @param params GenerateTokenSmsServiceRequestType
 * @returns Promise IMessageResponse
 */
export const getZipCode = (
  params: ZipCodeTypes.IZipCodeServiceRequest,
): Promise<ZipCodeTypes.IZipCodeServiceResponse> => {
  return Utils.callServiceMachine(Machines.onGetZipCode.machine(), params);
};

/**
 * @description Returns multiple configurable lists
 * @param params IListOfValuesServiceRequestType
 * @returns Promise<IListOfValuesServiceResponse>
 */
export const getListOfValues = (
  params: ListOfValuesTypes.IListOfValuesServiceRequestType,
): Promise<ListOfValuesTypes.IListOfValuesServiceResponse> => {
  return Utils.callServiceMachine(Machines.onGetListOfValues.machine(), params);
};

/**
 * @description Returns multiple configurable lists
 * @param params IListOfValuesServiceRequestType
 * @returns Promise<IListOfValuesServiceResponse>
 */
export const getCmsContent = (
  params: CmsContentTypes.CmsContentRequestType,
): Promise<CmsContentTypes.ICmsContentServiceResponse> => {
  return Utils.callServiceMachine(Machines.onGetCmsContent.machine(), params);
};

/**
 * @description Essa função busca conteúdos do CMS. Existem alguns conteúdos já mapeados,
 * determinados pelo dado enviado no parâmetro `content`.
 * Caso, porém, deseja-se consultar um conteúdo não mapeado, basta passar o id desse conteúdo
 * nesse mesmo parâmetro `content`.
 *
 * Nos casos de conteúdos conhecidos, a função já retorna os dados corretamente tipados.
 *
 * Porém, para conteúdos novos, o tipo de retorno é `unknown`. Dessa forma, espera-se que o cliente informe a tipagem de retorno através de um type casting
 * @param content Identificador do conteúdo a ser buscado.
 * @example
 * ```js
 * const knownResult = await getCmsDeliveryContent('COBRANDED_CARDS')
 *
 * const unknownResult = ( await getCmsDeliveryContent('a30ed242-b35c-9e404-1c36dd5') ) as { name: string }
 * ```
 */
export const getCmsDeliveryContent = <
  T extends keyof CmsDeliveryTypes.CmsDeliveryContentData,
>(
  content: T,
): Promise<CmsDeliveryTypes.CmsDeliveryContentData[T]> => {
  switch (content) {
    case 'COBRANDED_CARDS':
      return Utils.callServiceMachine(Machines.onGetCmsCobrandedCard.machine());

    case 'COBRANDED_COMMON_QUESTIONS':
      return Utils.callServiceMachine(
        Machines.onGetCmsCobrandedCommonQuestions.machine(),
      );

    case 'COBRANDED_USEFUL_PHONES':
      return Utils.callServiceMachine(
        Machines.onGetCmsCobrandedUsefulPhones.machine(),
      );

    case 'AIR_TRANSPORT_CONTRACT':
      return Utils.callServiceMachine(
        Machines.onGetCmsAirTransportContract.machine(),
      );
    case 'EMERGENCY_EXIT':
      return Utils.callServiceMachine(Machines.onGetCmsEmergencyExit.machine());

    case 'CLUB_SMILES_TERMS':
      return Utils.callServiceMachine(
        Machines.onGetCmsClubSmilesTerms.machine(),
      );

    default:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return {} as any;
  }
};

/**
 * @description Returns smiles partners and products
 */
export const getPartners = (
  params?: ListPartnerTypeTypes.ListPartnerTypeRequestType,
): Promise<ListPartnerTypeTypes.IListPartnerTypeServiceResponse> => {
  return Utils.callServiceMachine(Machines.onListPartnerType.machine(), params);
};

/**
 * @description Essa função busca conteúdos do CMS. Existem alguns conteúdos já mapeados,
 * determinados pela string enviada no parâmetro.
 * @params Identificador do conteúdo a ser buscado, podendo este ser uma string já mapeada quanto uma string desconhecida.
 * @example
 * ```js
 * const knownResult = await getCommonContent('TERMOS_UBER')
 *
 * const knownResult = await getCommonContent('UNKNOWN_STRING')
 * ```
 */
const getCommonContent = (
  params: CommonContentTypes.CommonContentParams,
): Promise<CommonContentTypes.IGetCommonContentServiceResponse> =>
  Utils.callServiceMachine(Machines.onGetCommonContent.machine(), {
    params,
  });

export default {
  getGeoLocation,
  findHeadlessDelivery,
  getHeadlessDelivery,
  partnerToken,
  uploadFileS3,
  getListCountry,
  getZipCode,
  getListOfValues,
  getCmsContent,
  getCmsDeliveryContent,
  getPartners,
  getCommonContent,
};
